import {ActionType, createReducer} from 'typesafe-actions';
import {actions} from '../../index';
import {APIErrorInterface} from '../../types';
import {
    AccountDetailsData,
    AccountListItem,
    AccountsListFilters,
    TransactionItem
} from '../../actions/accounts/payloads';

export type AccountsState = {
    accountsList: {
        total: number;
        activeTotal: number;
        items: AccountListItem[];
        filters: AccountsListFilters;
    };
    isLoading: boolean;
    isAdding: boolean;
    addErrors?: APIErrorInterface;
    editErrors?: APIErrorInterface;
    accountDetails?: AccountDetailsData;
    transactions: {
        total: number;
        items: TransactionItem[];
        isLoading: boolean;
    };
};

export const initialState: AccountsState = {
    accountsList: {
        total: 0,
        activeTotal: 0,
        items: [],
        filters: {},
    },
    isLoading: false,
    isAdding: false,
    transactions: {
        total: 0,
        items: [],
        isLoading: false,
    },
};

export type AccountsActions = ActionType<typeof actions>;

export const accountsReducer = createReducer<AccountsState, AccountsActions>(initialState)
    .handleAction(actions.getAccountsList.request, (state) => ({
        ...state,
        isLoading: true,
    }))
    .handleAction(actions.getAccountsList.success, (state, action) => ({
        ...state,
        isLoading: false,
        accountsList: { ...action.payload },
    }))
    .handleAction(actions.getAccountsList.failure, (state) => ({
        ...state,
        isLoading: false,
    }))
    .handleAction(actions.addAccount.request, (state) => ({
        ...state,
        isAdding: true,
        addErrors: undefined,
    }))
    .handleAction(actions.addAccount.success, (state) => ({
        ...state,
        isAdding: false,
    }))
    .handleAction(actions.addAccount.failure, (state, action) => ({
        ...state,
        isAdding: false,
        addErrors: action.payload,
    }))
    .handleAction(actions.clearAddAccountErrors, (state) => ({
        ...state,
        addErrors: undefined,
    }))
    .handleAction(actions.getAccountDetails.request, (state) => ({
        ...state,
        accountDetails: {
            isFormDataLoading: true,
        } as AccountDetailsData,
    }))
    .handleAction(actions.getAccountDetails.success, (state, action) => ({
        ...state,
        accountDetails: {
            ...action.payload,
            isFormDataLoading: false,
        },
    }))
    .handleAction(actions.getAccountDetails.failure, (state, action) => ({
        ...state,
        accountDetails: {
            isFormDataLoading: false,
            apiError: action.payload,
        } as AccountDetailsData,
    }))
    .handleAction(actions.editAccount.request, (state) => ({
        ...state,
        editErrors: undefined,
        accountDetails: state.accountDetails
            ? { ...state.accountDetails, isFormSending: true }
            : state.accountDetails,
    }))
    .handleAction(actions.editAccount.success, (state) => ({
        ...state,
        accountDetails: state.accountDetails
            ? { ...state.accountDetails, isFormSending: false }
            : state.accountDetails,
    }))
    .handleAction(actions.editAccount.failure, (state, action) => ({
        ...state,
        editErrors: action.payload,
        accountDetails: state.accountDetails
            ? { ...state.accountDetails, isFormSending: false }
            : state.accountDetails,
    }))
    .handleAction(actions.clearEditAccountErrors, (state) => ({
        ...state,
        editErrors: undefined,
    }))
    .handleAction(actions.setExtensionStatus.success, (state, action) => ({
        ...state,
        accountDetails: state.accountDetails
            && state.accountDetails.i_account?.toString() === action.payload.account_info.i_account.toString()
            ? {
                ...state.accountDetails,
                blocked: action.payload.account_info.blocked,
            }
            : state.accountDetails,
    }))
    .handleAction(actions.getAccountTransactions.request, (state) => ({
        ...state,
        transactions: { ...state.transactions, isLoading: true, items: [], total: 0 },
    }))
    .handleAction(actions.getAccountTransactions.success, (state, action) => ({
        ...state,
        transactions: {
            isLoading: false,
            total: action.payload.total,
            items: action.payload.items,
        },
    }))
    .handleAction(actions.getAccountTransactions.failure, (state) => ({
        ...state,
        transactions: { ...state.transactions, isLoading: false },
    }));

export default accountsReducer;
